import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, SlidersHorizontal } from "lucide-react";
import { api } from "@/lib/api";
import { portfolioFallback } from "@/data/marketing";

const categories = ["all", "local-business", "e-commerce", "saas", "portfolio", "corporate", "blogs", "landing"];
const categoryLabel = (value) => value.replace(/-/g, " ");

export default function Portfolio() {
  const [items, setItems] = useState(portfolioFallback);
  const [category, setCategory] = useState("all");

  useEffect(() => {
    api.get("/portfolio")
      .then(({ data }) => {
        if (Array.isArray(data) && data.length) setItems(data);
      })
      .catch(() => {});
  }, []);

  const filtered = useMemo(() => (
    category === "all" ? items : items.filter((item) => item.category === category)
  ), [category, items]);

  return (
    <div className="marketing-page" data-testid="portfolio-page">
      <header className="page-container pt-16 sm:pt-24 pb-12 sm:pb-16">
        <div className="grid lg:grid-cols-12 gap-8 items-end">
          <div className="lg:col-span-8">
            <p className="eyebrow">Selected work</p>
            <h1 className="font-display font-semibold text-5xl sm:text-7xl lg:text-[92px] leading-[0.92] tracking-[-0.06em]">
              Proof, not just polish.
            </h1>
          </div>
          <p className="lg:col-span-4 text-white/60 leading-relaxed max-w-lg">
            A selection of product, commerce and brand experiences. Each case looks different because each business problem was different.
          </p>
        </div>
      </header>

      <div className="border-y border-white/10">
        <div className="page-container py-5 flex flex-col sm:flex-row sm:items-center gap-4 sm:justify-between">
          <div className="flex items-center gap-2 text-xs font-semibold text-white/45 uppercase tracking-[0.12em]">
            <SlidersHorizontal size={14} aria-hidden="true" /> Filter the work
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1" role="group" aria-label="Filter work by category">
            {categories.map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => setCategory(item)}
                aria-pressed={category === item}
                data-testid={`filter-cat-${item}`}
                className={`shrink-0 px-4 py-2 rounded-full border text-xs font-semibold capitalize transition-colors ${category === item ? "bg-[#D8FF5B] text-[#11130F] border-[#D8FF5B]" : "border-white/15 text-white/60 hover:text-white hover:border-white/40"}`}
              >
                {categoryLabel(item)}
              </button>
            ))}
          </div>
        </div>
      </div>

      <section className="page-container py-12 sm:py-16 pb-24 sm:pb-32" aria-live="polite">
        {filtered.length ? (
          <div className="grid md:grid-cols-2 gap-x-5 gap-y-12 sm:gap-y-16">
            {filtered.map((item, index) => (
              <motion.article
                key={item.item_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: Math.min(index * 0.06, 0.24) }}
                className={index % 3 === 1 ? "md:pt-16" : ""}
              >
                <Link to={`/portfolio/${item.item_id}`} className="group block" data-testid={`portfolio-card-${item.item_id}`}>
                  <div className="relative overflow-hidden rounded-xl aspect-[4/3] bg-[#151815] border border-white/10">
                    <img src={item.cover} alt="" className="w-full h-full object-cover saturate-[.72] group-hover:saturate-100 group-hover:scale-[1.035] transition-transform duration-500" loading={index < 2 ? "eager" : "lazy"} />
                    <span className="absolute top-4 right-4 w-12 h-12 rounded-full bg-[#D8FF5B] text-[#11130F] grid place-items-center opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-opacity">
                      <ArrowUpRight size={19} aria-hidden="true" />
                    </span>
                  </div>
                  <div className="pt-5">
                    <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[.12em] text-white/40">
                      <span>{categoryLabel(item.category)}</span><span>·</span><span>{item.industry || item.style}</span>
                    </div>
                    <h2 className="font-display text-2xl sm:text-3xl font-semibold mt-2 group-hover:text-[#D8FF5B] transition-colors">{item.title}</h2>
                    <p className="text-white/55 mt-2 leading-relaxed max-w-xl">{item.summary}</p>
                  </div>
                </Link>
              </motion.article>
            ))}
          </div>
        ) : (
          <div className="border border-white/10 rounded-xl p-10 text-center" data-testid="portfolio-empty">
            <h2 className="font-display text-2xl">No projects in this category yet.</h2>
            <button type="button" onClick={() => setCategory("all")} className="pill-btn ghost mt-5">View all work</button>
          </div>
        )}
      </section>
    </div>
  );
}
